import { Injectable } from '@angular/core';
import { HttpContextToken, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { ApiBaseUrls, ApiSettings } from '../constants/constants';
import { ArrayUtilities } from '../utilities/array.utilities';
import { CommonChecks } from '../utilities/common-checks.utilities';
import { CacheService } from '../services/cache.service';

// Set on a request whose caller needs the server's current answer rather than a cached one - the
// maintenance poll, which would otherwise be answered from a copy taken before maintenance ended.
export const BYPASS_CACHE = new HttpContextToken<boolean>(() => false);

@Injectable()
export class CacheInterceptor implements HttpInterceptor {

  constructor(private cacheService: CacheService) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {    
    // Exit interceptor if not an API call    
    if (request.url.includes(ApiBaseUrls.AUTHENTICATION()) === false &&
        request.url.includes(ApiBaseUrls.CORE()) === false) {
      return next.handle(request);
    }
    
    if (request.context.get(BYPASS_CACHE)) {
      return next.handle(request);
    }

    const key = CacheInterceptor.getUniqueMapKey(request);

    // Only cache GET and "filter" POST requests
    if (CacheInterceptor.isCacheable(request) == false) {  
      const invalidateCacheIndices = CacheInterceptor.shouldInvalidateCache(request.url, this.cacheService.getCache());
      if (invalidateCacheIndices.length > 0) {
        CacheInterceptor.invalidateCache(this.cacheService.getCache(), invalidateCacheIndices);
      }

      return next.handle(request);
    } 

    const cachedResponse = this.cacheService.get(key);
    if (cachedResponse) {
      // Return cached response if available
      return of(cachedResponse);
    }

    // If not cached, proceed with the request and cache the response
    return CacheInterceptor.cacheData(this.cacheService.getCache(), request, next);
  }

  public static isCacheable(request: HttpRequest<any>): boolean {
    if (request.method == "GET" || (request.method === "POST" && request.urlWithParams.includes("filter") === true)) {
      return true;
    }
    return false;
  }

public static shouldInvalidateCache(url: string, cache: Map<string, HttpResponse<any>>): string[] {
    const controllerName = '/' + CacheInterceptor.getAPIControllerPath(url) + '/';
    const invalidateCacheKeys: string[] = [];

    invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, controllerName));
    
    if ((/\/properties\/[^\/]+\/members/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/members/'));
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/charges/'));
    }

    // A member's or user's authorityLevelRequired (which gates Activate/Deactivate/Delete/Edit
    // actions) is derived from MembersToRole/UsersToRole and changes via any /members/ or /users/
    // mutation (role grants, status changes, deletes, ownership transfers) — not just the controller
    // being mutated. Without this, a stale pre-mutation /authorization/{userId} response can be
    // served from cache for up to the cache TTL after the mutation.
    if ((/\/(members|users)(?:\/|$)/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/authorization/'));
    }

    // The mirror of the rule above, and it crosses APIs. Granting or revoking a staff role posts to
    // the Core API's /authorization/{userId}/roles, but what it changes is how that user is LISTED
    // by the Authentication API: users/filter returns mostPermissiveRole and supports the roleIds
    // and hasRole filters. Only the authorization controller's own entries are evicted by the
    // controller-name rule, so without this Staff Management re-runs its search after an assign or
    // unassign and is handed back its own pre-change response — the row it just changed looks
    // untouched, and the picker keeps offering someone who now holds a role (DEV-2672).
    if ((/\/authorization\/\d+\/roles(?:\/|$)/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/users/'));
    }

    if ((/\/payments(?:\/|$)/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/charges/'));
    }

    if ((/\/charges(?:\/|$)/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/payments/'));
    }

    // The header buckets (the reconciliation summary) live under the bank-connections controller, but
    // they are derived from that connection's transactions and change on every reconciliation write —
    // match, exclude, transfer and undo, all of which post to bank-transactions. Those only evict
    // their own controller, so without this the buckets serve a stale summary for up to the cache TTL
    // while the lists beside them, fetched by a filter POST under the mutated controller, are current.
    if ((/\/bank-transactions(?:\/|$)/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/bank-connections/'));
    }

    // The org storage-usage total lives under the organizations controller, but it changes on every
    // document/folder mutation (upload commit, delete, move, trash). Those mutations only invalidate
    // their own controller's cache, so without this the usage bar serves a stale total for up to the
    // cache TTL. Evict the storage-usage entry whenever a document or folder mutation occurs.
    if ((/\/(documents|folders)(?:\/|$)/).test(url)) {
      invalidateCacheKeys.push(...ArrayUtilities.mapContainsKeyWithPartialString(cache, '/storage-usage'));
    }

    return invalidateCacheKeys;
  }

  public static cacheData(cache: Map<string, HttpResponse<any>>, request: HttpRequest<any>, next: HttpHandler):  Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse && CacheInterceptor.isCacheable(request)) {
          const mapKey = CacheInterceptor.getUniqueMapKey(request);
          cache.set(mapKey, event);
          CacheInterceptor.setCacheExpiration(mapKey, cache);
        }
      })
    );
  }

  public static invalidateCache(cache: Map<string, HttpResponse<any>>, keysToInvalidate: string[]): void {
    keysToInvalidate.forEach(key => {
      cache.delete(key);
    });
  }

  public static getAPIControllerPath(url: string): string {
    const constrollerRouteStart = url.indexOf("/",8) + 1;
    let controllerRouteEnd: number | undefined = url.indexOf("/", constrollerRouteStart + 1);
    //Substrings second param is optional and need to be undefined to be ignored
    if (controllerRouteEnd == -1) { controllerRouteEnd = undefined};
    const controllerRoute = url.substring(constrollerRouteStart, controllerRouteEnd);
    return controllerRoute;
  }

  public static getUniqueMapKey(request: HttpRequest<any>): string {
    let requestBody = "";
    if (!CommonChecks.isNullOrUndefinedOrEmpty(request.body) && JSON.stringify(request.body)) {
      requestBody = JSON.stringify(request.body).toLowerCase();
    } 
    const key = request.urlWithParams + requestBody;
    return key;
  }

  public static async setCacheExpiration(mapKey: string, cache: Map<string, HttpResponse<any>>) : Promise<void> {
    setTimeout( () => {
      const invalidateCacheKeys = CacheInterceptor.shouldInvalidateCache(mapKey, cache);
      CacheInterceptor.invalidateCache(cache, invalidateCacheKeys);
    }, ApiSettings.DEFAULT_CACHE_TIMEOUT_MS());
  }

}
